import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import QueueIcon from '@mui/icons-material/Queue';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tab,
  CircularProgress,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { Table, TableRef } from '../../../components/Table/Table';
import { useAppDispatch } from '../../../redux/hooks';
import { fetchOnboardingEmployees } from '../../../redux/modules/b2bThriftPlan/employees/onboardingEmployee.actions';
import {
  onboardingEmployeeSelector,
  onboardingEmployeesLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/employees/onboardingEmployee.selectors';
import {
  uploadCompanyEmployeesCsv,
  deleteCompanyEmployee,
} from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.actions';
import { companyEmployeeUploadCsvLoadingSelector } from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.selectors';
import { CompanyEmployees } from './CompanyEmployees';
import { locations } from '../../../routes/locations';
import { OnboardingEmployeeStatus } from '../../../types/b2tRatibiPlus';
import { useSearchParamsState } from '../../../hooks';
import { MTabPanel } from '../../../components/TabPanel';
import { downloadCsvTemplateByName } from '../../../utils/csvTemplates';

const mapTabParamToValue: { [prop: string]: string } = {
  all: '0',
  queued: '1',
  processing: '2',
  completed: '3',
  failed: '4',
  default: '0',
};

const mapTabValueToTabParam: { [prop: string]: string } = {
  0: 'all',
  1: 'queued',
  2: 'processing',
  3: 'completed',
  4: 'failed',
};

export const OnboardingEmployees = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { onboardingEmployees, totalOnboardingEmployees } = useSelector(onboardingEmployeeSelector);
  const isOnboardingLoading = useSelector(onboardingEmployeesLoadingSelector);
  const isUploadLoading = useSelector(companyEmployeeUploadCsvLoadingSelector);
  const [selectedTab, setSelectedTab] = useSearchParamsState('tab', 'default');
  const tableRef = useRef<TableRef>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloadTemplateLoading, setIsDownloadTemplateLoading] = useState(false);

  const currentTab = mapTabValueToTabParam[mapTabParamToValue[selectedTab]];
  const isLoading = isOnboardingLoading;

  const onAddNew = (): void => {
    if (companyId) {
      navigate(locations.companyEmployee(Number(companyId), 'create'));
    }
  };

  const onUploadCsv = (): void => {
    setUploadDialogOpen(true);
  };

  const onDownloadCsvTemplate = async (): Promise<void> => {
    try {
      setIsDownloadTemplateLoading(true);
      await downloadCsvTemplateByName('company_group_employees');
    } catch (error: any) {
      const message = error?.message || 'Failed to download CSV template.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsDownloadTemplateLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !companyId) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }

    try {
      await dispatch(uploadCompanyEmployeesCsv({ companyId: Number(companyId), file: selectedFile })).unwrap();
      enqueueSnackbar('CSV file uploaded successfully!', { variant: 'success' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh the table
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Upload failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(mapTabValueToTabParam[newValue]);
  };

  const getStatusColor = (status: OnboardingEmployeeStatus) => {
    switch (status) {
      case 'queued':
        return 'info';
      case 'processing':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const onboardingColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      width: 70,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'phone_number',
      headerName: 'Phone Number',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'national_id',
      headerName: 'National ID',
      width: 130,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as OnboardingEmployeeStatus;
        return <Chip label={status} color={getStatusColor(status)} size="small" />;
      },
    },
    {
      field: 'error_message',
      headerName: 'Error Message',
      width: 300,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'batch_id',
      headerName: 'Batch ID',
      width: 200,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'row_index',
      headerName: 'Row Index',
      width: 100,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  const handleEditEmployee = (employeeId: number) => {
    if (companyId) {
      navigate(locations.companyEmployee(Number(companyId), 'edit', employeeId));
    }
  };

  const handleDeleteEmployee = async (employeeId: number) => {
    if (!companyId) return;

    try {
      await dispatch(deleteCompanyEmployee({ companyId: Number(companyId), employeeId })).unwrap();
      enqueueSnackbar('Company employee deleted successfully!', { variant: 'success' });
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Delete failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Onboarding Employees &nbsp;
          <Fab color="primary" size="small" aria-label="Create Employee" onClick={onAddNew} sx={{ mr: 1 }}>
            <AddIcon />
          </Fab>
          <Fab
            color="secondary"
            size="small"
            aria-label="Upload CSV"
            onClick={onUploadCsv}
            sx={{ color: 'common.white' }}
          >
            <UploadFileIcon />
          </Fab>
          <Fab
            color="secondary"
            size="small"
            aria-label="Download CSV Template"
            onClick={onDownloadCsvTemplate}
            sx={{ mx: 1 }}
            disabled={isDownloadTemplateLoading}
          >
            {isDownloadTemplateLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SimCardDownloadOutlinedIcon sx={{ color: 'common.white' }} />
            )}
          </Fab>
        </Typography>
      </Box>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle>Upload Company Employees CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              aria-label="Select CSV file"
              color="primary"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Select CSV File'}
              <input ref={fileInputRef} hidden accept=".csv,text/csv" type="file" onChange={handleFileChange} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} color="secondary">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleUpload}
            variant="contained"
            color="primary"
            loading={isUploadLoading}
            disabled={!selectedFile}
          >
            Upload
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <TabContext value={mapTabParamToValue[selectedTab]}>
        <Box>
          <TabList
            value={mapTabParamToValue[selectedTab]}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="onboarding employees status tabs"
          >
            <Tab
              icon={<AllInclusiveIcon />}
              iconPosition="start"
              label="All"
              value="0"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<QueueIcon />}
              iconPosition="start"
              label="Queued"
              value="1"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<RefreshIcon />}
              iconPosition="start"
              label="Processing"
              value="2"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<CheckCircleIcon />}
              iconPosition="start"
              label="Completed"
              value="3"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ErrorIcon />}
              iconPosition="start"
              label="Failed"
              value="4"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
          </TabList>
        </Box>
        <Box sx={{ position: 'relative', width: '100%', minHeight: '400px' }}>
          <MTabPanel value="0">
            {(currentTab === 'all' || currentTab === undefined) && (
              <Table
                ref={tableRef}
                columns={onboardingColumns}
                rows={onboardingEmployees}
                rowCount={totalOnboardingEmployees}
                fetchItems={fetchOnboardingEmployees}
                fetchItemsCustomParams={{
                  companyId: Number(companyId),
                }}
                loading={isOnboardingLoading}
                initialSortModel={[{ field: 'id', sort: 'desc' }]}
                toolbarExportProps={{
                  csvOptions: { fileName: `onboarding-employees-all-${Date.now()}` },
                  excelOptions: { fileName: `onboarding-employees-all-${Date.now()}` },
                }}
              />
            )}
          </MTabPanel>
          <MTabPanel value="1">
            {currentTab === 'queued' && (
              <Table
                ref={tableRef}
                columns={onboardingColumns}
                rows={onboardingEmployees}
                rowCount={totalOnboardingEmployees}
                fetchItems={fetchOnboardingEmployees}
                fetchItemsCustomParams={{
                  companyId: Number(companyId),
                  filterBy: 'status',
                  filterValue: 'queued',
                }}
                loading={isOnboardingLoading}
                initialSortModel={[{ field: 'id', sort: 'desc' }]}
                toolbarExportProps={{
                  csvOptions: { fileName: `onboarding-employees-queued-${Date.now()}` },
                  excelOptions: { fileName: `onboarding-employees-queued-${Date.now()}` },
                }}
              />
            )}
          </MTabPanel>
          <MTabPanel value="2">
            {currentTab === 'processing' && (
              <Table
                ref={tableRef}
                columns={onboardingColumns}
                rows={onboardingEmployees}
                rowCount={totalOnboardingEmployees}
                fetchItems={fetchOnboardingEmployees}
                fetchItemsCustomParams={{
                  companyId: Number(companyId),
                  filterBy: 'status',
                  filterValue: 'processing',
                }}
                loading={isOnboardingLoading}
                initialSortModel={[{ field: 'id', sort: 'desc' }]}
                toolbarExportProps={{
                  csvOptions: { fileName: `onboarding-employees-processing-${Date.now()}` },
                  excelOptions: { fileName: `onboarding-employees-processing-${Date.now()}` },
                }}
              />
            )}
          </MTabPanel>
          <MTabPanel value="3">
            {currentTab === 'completed' && (
              <CompanyEmployees
                showHeader={false}
                showUploadButton={false}
                showCreateButton
                onEdit={handleEditEmployee}
                onDelete={handleDeleteEmployee}
              />
            )}
          </MTabPanel>
          <MTabPanel value="4">
            {currentTab === 'failed' && (
              <Table
                ref={tableRef}
                columns={onboardingColumns}
                rows={onboardingEmployees}
                rowCount={totalOnboardingEmployees}
                fetchItems={fetchOnboardingEmployees}
                fetchItemsCustomParams={{
                  companyId: Number(companyId),
                  filterBy: 'status',
                  filterValue: 'failed',
                }}
                loading={isOnboardingLoading}
                initialSortModel={[{ field: 'id', sort: 'desc' }]}
                toolbarExportProps={{
                  csvOptions: { fileName: `onboarding-employees-failed-${Date.now()}` },
                  excelOptions: { fileName: `onboarding-employees-failed-${Date.now()}` },
                }}
              />
            )}
          </MTabPanel>
        </Box>
      </TabContext>
    </Container>
  );
};
